import React from "react"
import {
    Box,
    ConditionalRenderContainer,
    useTheme,
    ExternalLink,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation"

import { getListingTypeCamelcase } from "~utils/helpers"
import { ListingType } from "~graphql/generated/graphql"
import type {
    AddressFieldsFragment,
    LicenseInfoFieldsFragment,
    ListingFieldsFragment,
    PublicListingFieldsFragment,
} from "~graphql/generated/graphql"
import { isBlastCastType, isHuntingType } from "~utils/listing-helpers"
import COPY from "~config/copy-constants"
import TextWithExternalLinks from "~components/shared/text-with-external-links"
import Divider from "~components/shared/divider"

const SOUTH_AFRICA = "South Africa"

interface Props {
    readonly listing?:
        | ListingFieldsFragment
        | PublicListingFieldsFragment
        | null
}

export default function ListingLicense({ listing }: Props) {
    const styles = useStyles()
    const { colors } = useTheme()

    if (!listing?.license_required) return null

    const copyConfigs = getCopyConfigs(listing)

    return (
        <>
            <Box style={styles.license}>
                <Box style={styles.licenseHeader}>
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        color={colors.warningColor}
                        fontSize={28}
                    />
                    <Box style={styles.licenseTitle}>
                        {COPY.LICENSE_REQUIRED}
                    </Box>
                </Box>
                <Box style={styles.paragraph}>
                    <TextWithExternalLinks configs={copyConfigs} />
                </Box>
                <ConditionalRenderContainer
                    shouldRender={Boolean(
                        listing.license_info?.online_portal_url
                    )}
                    style={styles.link}
                >
                    <ExternalLink
                        href={listing.license_info?.online_portal_url!}
                        isUnderlined
                    >
                        {COPY.OBTAIN_LICENSE}
                    </ExternalLink>
                </ConditionalRenderContainer>
            </Box>
            <Divider />
        </>
    )
}

function useStyles() {
    const { colors } = useTheme()

    return {
        link: {
            marginTop: "24px",
        },
        paragraph: {
            fontWeight: 300,
            lineHeight: "24px",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        license: {
            border: `1px solid ${colors.warningColor}`,
            padding: "24px",
            marginY: 4,
            borderRadius: "10px",
        },
        licenseHeader: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginBottom: "8px",
            gap: 3,
        },
        licenseIcon: {
            width: "34px",
            height: "34px",
            marginRight: "8px",
            color: colors.warningColor,
        },
        licenseTitle: {
            fontSize: "24px",
            fontWeight: 500,
            color: colors.warningColor,
        },
    }
}

function getHuntingLicenseUrl(
    licenseInfo: LicenseInfoFieldsFragment | null | undefined
) {
    return (
        licenseInfo?.hunting_info_url ?? licenseInfo?.general_info_url ?? null
    )
}

function getFishingLicenseUrl(
    licenseInfo: LicenseInfoFieldsFragment | null | undefined
) {
    return (
        licenseInfo?.fishing_info_url ?? licenseInfo?.general_info_url ?? null
    )
}

function getLicenseCountryAndState(
    location: AddressFieldsFragment | null | undefined
) {
    const licenseCountry = location?.country
    const licenseState = licenseCountry === SOUTH_AFRICA ? "" : location?.state

    if (licenseCountry && licenseState) {
        return `${licenseState}, ${licenseCountry}`
    }

    return licenseState ?? licenseCountry
}

function getCopyConfigs(
    listing: ListingFieldsFragment | PublicListingFieldsFragment
) {
    const listingType = listing.type ?? ListingType.BlastCast
    const huntingLicenseUrl = getHuntingLicenseUrl(listing.license_info)
    const fishingLicenseUrl = getFishingLicenseUrl(listing.license_info)
    const licenseUrl = isHuntingType(listingType)
        ? huntingLicenseUrl
        : fishingLicenseUrl
    const licenseCountryAndState = getLicenseCountryAndState(listing.location)

    const copyConfigs = isBlastCastType(listingType)
        ? getBlastCastCopyConfigs({ huntingLicenseUrl, fishingLicenseUrl })
        : getNonBlastCaseCopyConfigs({ listingType, licenseUrl })
    const fromConfig = {
        text: ` ${COPY.FROM} ${licenseCountryAndState}`,
        url: null,
    }

    return [...copyConfigs, ...(licenseCountryAndState ? [fromConfig] : [])]
}

function getBlastCastCopyConfigs({
    huntingLicenseUrl,
    fishingLicenseUrl,
}: {
    huntingLicenseUrl: string | null
    fishingLicenseUrl: string | null
}) {
    return [
        { text: COPY.TRIP_REQUIRES_BOTH },
        { text: COPY.HUNTING_LICENSE, url: huntingLicenseUrl },
        { text: COPY.AND_A },
        { text: COPY.FISHING_LICENSE, url: fishingLicenseUrl },
    ]
}

function getNonBlastCaseCopyConfigs({
    listingType,
    licenseUrl,
}: {
    listingType: ListingType
    licenseUrl: string | null
}) {
    const formattedListingType = getListingTypeCamelcase(listingType)

    return [
        { text: COPY.TRIP_REQUIRES, url: null },
        {
            text: `${formattedListingType.toLowerCase()} ${COPY.LICENSE}`,
            url: licenseUrl,
        },
    ]
}
